/** CIY mix-and-match selector. Checkout discounts are configured in Shopify, never in this script. */
const copy = {
  en: {
    heading: 'Choose your set', shirt: 'Shirt', shirts: 'Shirts', save: 'Save', from: 'From',
    single: 'Your everyday essential', two: 'Free shipping in Austria', three: 'Free shipping in Austria + Europe',
    intro: 'Mix colours, fits and prints. Choose a size for each shirt.', model: 'Model', size: 'Size',
    choose: 'Choose a size', sold: 'Sold out', total: 'Your set', saving: 'You save', button: 'Add set to cart',
    busy: 'Adding your set…', missing: 'Please choose an available size for every shirt.',
    failed: 'Your set could not be added. Check your cart before trying again.', cart: 'View cart',
    footnote: 'Free shipping: Austria from 2 items; other European delivery countries from 3. The delivery address at checkout applies.',
    editor: 'Preview: set up Shopify discounts and shipping rules to match your offer, then enable “SC · CIY Bundles” in theme settings.',
    preview: 'Preview — activate after setting up discounts',
  },
  de: {
    heading: 'Wähle dein Set', shirt: 'Shirt', shirts: 'Shirts', save: 'Spare', from: 'Ab',
    single: 'Dein Everyday Essential', two: 'Gratisversand in Österreich', three: 'Gratisversand in Österreich + Europa',
    intro: 'Kombiniere Farben, Schnitte und Prints. Wähle für jedes Shirt eine Größe.', model: 'Modell', size: 'Größe',
    choose: 'Größe wählen', sold: 'Ausverkauft', total: 'Dein Set', saving: 'Du sparst', button: 'Set in den Warenkorb',
    busy: 'Set wird hinzugefügt…', missing: 'Bitte wähle für jedes Shirt eine verfügbare Größe.',
    failed: 'Das Set konnte nicht hinzugefügt werden. Prüfe deinen Warenkorb, bevor du es erneut versuchst.', cart: 'Warenkorb ansehen',
    footnote: 'Gratisversand: Österreich ab 2 Artikeln; übrige europäische Lieferländer ab 3. Es gilt die Lieferadresse im Checkout.',
    editor: 'Vorschau: Zuerst die Shopify-Rabatte und Versandregeln passend zu deinem Angebot einrichten. Danach „SC · CIY Bundles“ in den Theme-Einstellungen aktivieren.',
    preview: 'Vorschau — nach Einrichtung der Rabatte aktivieren',
  },
};

export function bundleCopy(locale, texts = {}) {
  const words = { ...copy[locale?.startsWith('de') ? 'de' : 'en'] };
  // Explicitly empty settings hide the message; never restore an old shipping promise.
  for (const key of ['two', 'three', 'footnote']) {
    if (typeof texts[key] === 'string') words[key] = texts[key].trim();
  }
  return words;
}

export function groupVariants(variants) {
  const grouped = new Map();
  for (const variant of variants) {
    const id = String(variant.id);
    const item = grouped.get(id);
    if (item) item.quantity += 1;
    else grouped.set(id, { id, quantity: 1, price: Number(variant.price) });
  }
  return [...grouped.values()];
}

export function setPrice(variants, percent) {
  const lines = groupVariants(variants);
  const original = lines.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const total = lines.reduce((sum, item) => sum + Math.round(item.price * item.quantity * (100 - percent) / 100), 0);
  return { original, total, saving: original - total };
}

export function formatModelTitle(title) {
  return title.replace(/CIY\s+Shirt\s*[-–—:]?\s*/i, '').replace(/^women['’]?s?['’]?\s+/i, 'Women’s · ');
}

function node(tag, className, text) {
  const element = document.createElement(tag);
  if (className) element.className = className;
  if (text !== undefined) element.textContent = text;
  return element;
}

async function bounded(promise, milliseconds = 8000) {
  let timer;
  try {
    return await Promise.race([
      promise,
      new Promise((_, reject) => { timer = setTimeout(() => reject(new Error('Cart update timed out')), milliseconds); }),
    ]);
  } finally { clearTimeout(timer); }
}

async function waitUntil(check, milliseconds = 1200) {
  const deadline = Date.now() + milliseconds;
  do {
    if (check()) return true;
    await new Promise(resolve => setTimeout(resolve, 40));
  } while (Date.now() < deadline);
  return false;
}

/** Use the existing Essential drawer first, then the native theme drawer. Never add items here. */
export async function openBundleCart(root, sections) {
  const app = window.essentialCartApi;
  if (typeof app?.refreshCart === 'function' && typeof app.openCart === 'function') {
    // Essential also observes cart/add.js. Refresh after the successful response so discounts and quantities are current.
    await bounded(app.refreshCart());
    app.openCart();
    if (typeof app.isOpen !== 'function') return true;
    return waitUntil(() => app.isOpen());
  }

  const drawer = document.querySelector('cart-drawer-component');
  if (!drawer) return false;
  // The app can initialise after the product module. Allow it to take over before opening the native drawer.
  if (document.querySelector('essential-cart-app-embed')) {
    const ready = await waitUntil(() => typeof window.essentialCartApi?.openCart === 'function'
      && typeof window.essentialCartApi?.refreshCart === 'function', 1200);
    if (ready) return openBundleCart(root, sections);
  }
  if (typeof drawer.open !== 'function') return false;
  const [{ CartAddEvent }, response] = await bounded(Promise.all([
    import('@theme/events'),
    fetch(`${root}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' }, cache: 'no-store' }),
  ]));
  if (!response.ok) return false;
  const cart = await bounded(response.json());
  document.dispatchEvent(new CartAddEvent(cart, 'sc-ciy-bundle', {
    source: 'sc-ciy-bundle', itemCount: cart.item_count, sections,
  }));
  // Respect the theme's render cycle; the event updates the drawer contents and the header count.
  await new Promise(resolve => requestAnimationFrame(resolve));
  const updatedDrawer = document.querySelector('cart-drawer-component');
  updatedDrawer?.open?.();
  return waitUntil(() => Boolean(document.querySelector('cart-drawer-component dialog[open]')));
}

export class CiyBundle extends (globalThis.HTMLElement || class {}) {
  connectedCallback() {
    if (this.initialized) {
      this.variantTarget?.addEventListener('variant:update', this.onVariantUpdate);
      return;
    }
    // The deferred module runs after the initial markup; this also supports theme-editor insertions.
    const data = this.querySelector('[data-sc-config]');
    if (!data) return;
    try {
      this.config = JSON.parse(data.textContent);
      this.products = this.config.products.filter(product => product.variants?.length);
      this.product = this.products.find(product => String(product.id) === String(this.config.productId));
      if (!this.product || !this.products.length) return;
      this.words = bundleCopy(this.config.locale, this.config.texts);
      this.money = new Intl.NumberFormat(this.config.locale || 'en', { style: 'currency', currency: this.config.currency || 'EUR' });
      this.count = 1;
      this.picks = Array.from({ length: 3 }, () => ({ productId: String(this.product.id), variantId: '' }));
      this.picks[0].variantId = String(this.config.variantId || '');
      this.build();
      this.initialized = true;
      this.dataset.ready = '';
      this.hidden = false;
      this.setCount(1);
      this.variantTarget = this.closest('.product-details');
      this.onVariantUpdate = event => {
        const variant = event.detail?.resource;
        if (variant && this.product.variants.some(item => String(item.id) === String(variant.id))) {
          this.config.variantId = String(variant.id);
          if (this.count === 1 && this.picks[0].productId === String(this.product.id)) this.picks[0].variantId = String(variant.id);
          this.refreshPrice();
        }
      };
      this.variantTarget?.addEventListener('variant:update', this.onVariantUpdate);
    } catch (error) {
      // Leave the original theme form fully usable if configuration cannot be read.
      this.hidden = true;
      this.removeAttribute('data-ready');
      console.error('CIY bundle configuration:', error);
    }
  }

  disconnectedCallback() {
    this.variantTarget?.removeEventListener('variant:update', this.onVariantUpdate);
  }

  format(cents) { return this.money.format(cents / 100); }
  getProduct(index) { return this.products.find(product => String(product.id) === this.picks[index].productId); }
  getVariant(index) { return this.getProduct(index)?.variants.find(variant => String(variant.id) === this.picks[index].variantId); }
  usable(variant) { return variant.available && Number(variant.min || 1) === 1 && Number(variant.increment || 1) === 1; }
  rate(count = this.count) { return Math.min(100, Math.max(0, Number(this.config.rates[count]) || 0)); }

  build() {
    const root = this.querySelector('[data-sc-content]');
    root.replaceChildren();
    if (!this.config.active) root.append(node('p', 'sc-bundle__editor', this.words.editor));
    const tiers = node('fieldset', 'sc-bundle__tiers');
    tiers.append(node('legend', 'sc-bundle__heading', this.words.heading));
    this.tierPrices = [];
    for (let count = 1; count <= 3; count += 1) {
      const label = node('label', 'sc-bundle__tier');
      const input = node('input', 'sc-bundle__radio');
      input.type = 'radio'; input.name = `${this.id}-count`; input.value = String(count); input.checked = count === 1;
      input.addEventListener('change', () => this.setCount(count));
      const card = node('span', 'sc-bundle__card');
      const content = node('span');
      const title = node('span', 'sc-bundle__title', `${count} ${count === 1 ? this.words.shirt : this.words.shirts}`);
      if (count > 1) title.append(node('span', 'sc-bundle__badge', `−${this.rate(count)}%`));
      content.append(title);
      const caption = this.words[['single', 'two', 'three'][count - 1]];
      if (caption) content.append(node('span', 'sc-bundle__caption', caption));
      const price = node('span', 'sc-bundle__price');
      this.tierPrices[count] = price;
      card.append(content, price); label.append(input, card); tiers.append(label);
    }
    root.append(tiers);
    this.panel = node('form', 'sc-bundle__panel');
    this.panel.id = `${this.id}-form`;
    this.panel.addEventListener('submit', event => this.submit(event));
    this.panel.append(node('p', 'sc-bundle__intro', this.words.intro));
    this.rows = node('div', 'sc-bundle__rows');
    this.panel.append(this.rows);
    this.summary = node('div', 'sc-bundle__summary');
    this.summary.append(node('span', '', this.words.total));
    this.total = node('strong', 'sc-bundle__total');
    this.total.setAttribute('aria-live', 'polite'); this.total.setAttribute('aria-atomic', 'true');
    this.summary.append(this.total);
    this.saving = node('p', 'sc-bundle__saving');
    this.error = node('div', 'sc-bundle__error'); this.error.hidden = true; this.error.setAttribute('role', 'alert');
    this.submitButton = node('button', 'sc-bundle__submit button-secondary', this.words.button);
    this.submitButton.type = 'submit';
    this.panel.append(this.summary, this.saving, this.error, this.submitButton);
    root.append(this.panel);
    if (this.words.footnote) root.append(node('p', 'sc-bundle__footnote', this.words.footnote));
  }

  setCount(count) {
    if (this.busy) return;
    const previous = this.count;
    this.count = count; this.dataset.count = String(count);
    this.querySelectorAll('.sc-bundle__radio').forEach(input => { input.checked = Number(input.value) === count; });
    this.panel.hidden = count === 1;
    this.error.hidden = true;
    if (count > 1) {
      // Pick up the size the customer selected in the native single-shirt form.
      if (previous === 1 && this.picks[0].productId === String(this.product.id)) {
        const id = this.closest('.product-details')?.querySelector('product-form-component input[name="id"]')?.value;
        if (id && this.product.variants.some(variant => String(variant.id) === id)) this.picks[0].variantId = id;
      }
      this.renderRows();
    }
    this.refreshPrice();
  }

  renderRows() {
    this.rows.replaceChildren();
    for (let index = 0; index < this.count; index += 1) {
      const product = this.getProduct(index);
      const fieldset = node('fieldset', 'sc-bundle__row'); fieldset.dataset.slot = String(index);
      fieldset.append(node('legend', '', `${this.words.shirt} ${index + 1}`));
      const content = node('div', 'sc-bundle__row-content');
      const img = node('img', 'sc-bundle__image'); img.alt = product.title; img.loading = 'lazy'; img.width = 58; img.height = 74;
      if (product.image) img.src = product.image;
      else img.style.visibility = 'hidden';
      const fields = node('div', 'sc-bundle__fields');
      const modelLabel = node('label', 'sc-bundle__label', this.words.model);
      const model = node('select'); model.dataset.model = String(index); model.setAttribute('aria-label', `${this.words.shirt} ${index + 1} — ${this.words.model}`);
      for (const entry of this.products) {
        const available = entry.variants.some(variant => this.usable(variant));
        const option = node('option', '', formatModelTitle(entry.title) + (available ? '' : ` — ${this.words.sold}`));
        option.value = String(entry.id); option.disabled = !available; option.selected = option.value === this.picks[index].productId;
        model.append(option);
      }
      model.addEventListener('change', () => {
        const previousTitle = this.getVariant(index)?.title;
        this.picks[index] = { productId: model.value, variantId: '' };
        const equivalent = this.getProduct(index).variants.find(variant => variant.title === previousTitle && this.usable(variant));
        if (equivalent) this.picks[index].variantId = String(equivalent.id);
        this.renderRows(); this.refreshPrice();
        this.rows.querySelector(`[data-model="${index}"]`)?.focus({ preventScroll: true });
      });
      modelLabel.append(model);
      const sizeLabel = node('label', 'sc-bundle__label', this.words.size);
      const size = node('select'); size.dataset.variant = String(index); size.required = true;
      size.setAttribute('aria-label', `${this.words.shirt} ${index + 1} — ${this.words.size}`);
      const placeholder = node('option', '', this.words.choose); placeholder.value = ''; size.append(placeholder);
      for (const variant of product.variants) {
        const option = node('option', '', `${variant.title}${this.usable(variant) ? '' : ` — ${this.words.sold}`}`);
        option.value = String(variant.id); option.disabled = !this.usable(variant); size.append(option);
      }
      size.value = this.picks[index].variantId;
      if (!this.usable(this.getVariant(index) || {})) size.value = '';
      this.picks[index].variantId = size.value;
      size.addEventListener('change', () => { this.picks[index].variantId = size.value; this.error.hidden = true; this.refreshPrice(); });
      sizeLabel.append(size); fields.append(modelLabel, sizeLabel); content.append(img, fields); fieldset.append(content); this.rows.append(fieldset);
    }
  }

  refreshPrice() {
    const allVariants = this.products.flatMap(product => product.variants).filter(variant => this.usable(variant));
    const lowest = [...allVariants].sort((a, b) => a.price - b.price)[0];
    for (let count = 1; count <= 3; count += 1) {
      const destination = this.tierPrices[count]; destination.replaceChildren();
      if (!lowest) continue;
      let variants = Array(count).fill(lowest);
      let estimated = true;
      if (count === 1) {
        variants = [this.product.variants.find(variant => String(variant.id) === String(this.config.variantId)) || this.product.variants[0]];
        estimated = false;
      } else if (count === this.count) {
        variants = this.picks.slice(0, count).map((pick, index) => this.getVariant(index) || this.getProduct(index).variants.filter(variant => this.usable(variant)).sort((a, b) => a.price - b.price)[0] || lowest);
        estimated = !this.picks.slice(0, count).every((pick, index) => this.usable(this.getVariant(index) || {}));
      }
      const price = setPrice(variants, this.rate(count));
      if (estimated) destination.append(node('small', '', this.words.from));
      destination.append(node('span', '', this.format(price.total)));
      if (!estimated && count > 1) destination.append(node('s', '', this.format(price.original)));
    }
    if (this.count === 1) return;
    const selections = this.picks.slice(0, this.count).map((pick, index) => this.getVariant(index));
    const ready = selections.every(variant => variant && this.usable(variant));
    if (ready) {
      const price = setPrice(selections, this.rate());
      this.total.textContent = this.format(price.total);
      this.saving.textContent = `${this.words.saving} ${this.format(price.saving)} (${this.rate()}%).`;
    } else {
      this.total.textContent = '—'; this.saving.textContent = this.words.missing;
    }
    this.submitButton.disabled = !ready || this.busy || !this.config.active;
    this.submitButton.textContent = this.busy ? this.words.busy : (!this.config.active ? this.words.preview : this.words.button);
  }

  showError(message) {
    this.error.replaceChildren(node('span', '', message + ' '));
    const link = node('a', '', this.words.cart); link.href = this.config.cartUrl; this.error.append(link);
    this.error.hidden = false;
  }

  async submit(event) {
    event.preventDefault();
    if (this.busy || !this.config.active || this.count < 2) return;
    const variants = this.picks.slice(0, this.count).map((pick, index) => this.getVariant(index));
    if (!variants.every(variant => variant && this.usable(variant))) { this.showError(this.words.missing); return; }
    const items = groupVariants(variants).map(({ id, quantity }) => ({ id, quantity }));
    this.busy = true; this.error.hidden = true;
    this.querySelectorAll('select, .sc-bundle__radio').forEach(input => { input.disabled = true; });
    this.setAttribute('aria-busy', 'true'); this.refreshPrice();
    const root = this.config.root.endsWith('/') ? this.config.root : `${this.config.root}/`;
    const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map(element => element.dataset.sectionId).filter(Boolean))].slice(0, 5);
    const payload = { items };
    if (sections.length) {
      payload.sections = sections;
      payload.sections_url = window.location.pathname;
    }
    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (!response.ok || result.status) {
        const detail = typeof result.description === 'string' ? result.description : this.words.failed;
        this.showError(detail);
        return;
      }
      // The items are already added. A drawer failure must never trigger another add request.
      let opened = false;
      try { opened = await openBundleCart(root, result.sections); } catch { /* Full cart is the safe fallback. */ }
      if (!opened) window.location.assign(this.config.cartUrl);
    } catch {
      // Do not retry: the server may already have added the items before a connection was lost.
      this.showError(this.words.failed);
    } finally {
      this.busy = false; this.removeAttribute('aria-busy');
      this.querySelectorAll('select, .sc-bundle__radio').forEach(input => { input.disabled = false; });
      this.refreshPrice();
    }
  }
}

if (globalThis.customElements && !customElements.get('sc-ciy-bundle')) customElements.define('sc-ciy-bundle', CiyBundle);
